import { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useQuery } from 'react-query'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import {
  Activity,
  CheckCircle2,
  Eye,
  Filter,
  FlaskConical,
  RefreshCw,
  ShieldAlert,
  XCircle,
} from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { cmsService } from '@services/api'
import { useAuthStore } from '@store/authStore'

type Submission = {
  id: string
  kind: string
  status: string
  entityId: string
  endpoint?: string | null
  trigger?: string | null
  httpStatus?: number | null
  latraStatusCode?: number | null
  latraMessage?: string | null
  latraReference?: string | null
  authAccepted?: boolean | null
  attemptCount?: number
  errorMessage?: string | null
  requestPayload?: unknown
  responseBody?: unknown
  createdAt: string
  completedAt?: string | null
}

function statusColor(status?: string) {
  switch (status) {
    case 'SUCCESS':
      return 'green'
    case 'FAILED':
      return 'red'
    case 'SKIPPED':
      return 'orange'
    case 'PROCESSING':
      return 'blue'
    default:
      return 'gray'
  }
}

function pretty(value: unknown) {
  try {
    return JSON.stringify(value ?? null, null, 2)
  } catch {
    return String(value)
  }
}

function formatWhen(value?: string | null) {
  if (!value) return '—'
  return new Date(value).toLocaleString('en-GB', { timeZone: 'Africa/Nairobi' })
}

export default function LatraActivityPage() {
  const { user } = useAuthStore()
  const detailDrawer = useDisclosure()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [kind, setKind] = useState('')
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [days, setDays] = useState(14)
  const [page, setPage] = useState(0)
  const pageSize = 25

  const analyticsQuery = useQuery(
    ['latra-submission-analytics', days],
    () => cmsService.getLatraSubmissionAnalytics(days),
    { retry: false, keepPreviousData: true }
  )

  const listQuery = useQuery(
    ['latra-submissions-page', kind, status, search, from, to, page],
    () =>
      cmsService.getLatraSubmissions({
        take: pageSize,
        skip: page * pageSize,
        ...(kind ? { kind } : {}),
        ...(status ? { status } : {}),
        ...(search ? { search } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      }),
    { retry: false, keepPreviousData: true }
  )

  const detailQuery = useQuery(
    ['latra-submission-detail', selectedId],
    () => cmsService.getLatraSubmission(selectedId!),
    { enabled: Boolean(selectedId), retry: false }
  )

  if (user?.role !== 'admin') {
    return (
      <Alert status="error" borderRadius="xl">
        <AlertIcon />
        <Box>
          <AlertTitle>Admin Access Required</AlertTitle>
          <AlertDescription>LATRA activity logs are restricted to admin users.</AlertDescription>
        </Box>
      </Alert>
    )
  }

  const analytics = analyticsQuery.data?.data
  const list = listQuery.data?.data
  const items: Submission[] = list?.items || []
  const total = list?.total || 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const selected: Submission | null = detailQuery.data?.data || null

  const kindChart = useMemo(() => {
    const byKind = analytics?.byKind || {}
    return Object.entries(byKind).map(([name, value]) => ({ name, value }))
  }, [analytics])

  const dayChart = analytics?.byDay || []
  const triggerChart = useMemo(() => {
    const byTrigger = analytics?.byTrigger || {}
    return Object.entries(byTrigger)
      .map(([name, value]) => ({ name, value: Number(value) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)
  }, [analytics])

  const openDetail = (id: string) => {
    setSelectedId(id)
    detailDrawer.onOpen()
  }

  return (
    <VStack align="stretch" spacing={6}>
      <HStack justify="space-between" align="start" flexWrap="wrap" gap={3}>
        <Box>
          <Heading size="lg">LATRA Activity</Heading>
          <Text color="gray.600" mt={2}>
            Full audit of every LATRA interaction — filters, individual payloads, and success analysis.
          </Text>
        </Box>
        <HStack>
          <Button as={RouterLink} to="/latra-test" leftIcon={<FlaskConical size={16} />} variant="outline">
            Open Test Unit
          </Button>
          <Button
            leftIcon={<RefreshCw size={16} />}
            onClick={() => {
              analyticsQuery.refetch()
              listQuery.refetch()
            }}
            isLoading={analyticsQuery.isFetching || listQuery.isFetching}
          >
            Refresh
          </Button>
        </HStack>
      </HStack>

      <HStack>
        <Text fontSize="sm" color="gray.600">Analytics window</Text>
        <Select maxW="140px" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={7}>7 days</option>
          <option value={14}>14 days</option>
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
        </Select>
      </HStack>

      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
        <Card borderRadius="2xl">
          <CardBody>
            <Stat>
              <StatLabel>Total</StatLabel>
              <StatNumber>{analytics?.total ?? '—'}</StatNumber>
              <StatHelpText>Last {days} days</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card borderRadius="2xl">
          <CardBody>
            <Stat>
              <StatLabel>Success rate</StatLabel>
              <StatNumber color="green.500">{analytics?.successRate ?? 0}%</StatNumber>
              <StatHelpText>{analytics?.counts?.success ?? 0} successful</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card borderRadius="2xl">
          <CardBody>
            <Stat>
              <StatLabel>Failed</StatLabel>
              <StatNumber color="red.500">{analytics?.counts?.failed ?? 0}</StatNumber>
              <StatHelpText>{analytics?.failureRate ?? 0}% failure rate</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card borderRadius="2xl">
          <CardBody>
            <Stat>
              <StatLabel>Skipped</StatLabel>
              <StatNumber color="orange.500">{analytics?.counts?.skipped ?? 0}</StatNumber>
              <StatHelpText>Kill-switch / gated</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card borderRadius="2xl">
          <CardBody>
            <Stat>
              <StatLabel>Auth accepted</StatLabel>
              <StatNumber>{analytics?.auth?.accepted ?? 0}</StatNumber>
              <StatHelpText>Rejected: {analytics?.auth?.rejected ?? 0}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <Card borderRadius="2xl">
          <CardHeader>
            <HStack>
              <Activity size={18} />
              <Heading size="md">Daily volume</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            {analyticsQuery.isLoading ? (
              <Spinner />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={dayChart}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="success" stackId="a" fill="#22C55E" name="Success" />
                  <Bar dataKey="failed" stackId="a" fill="#EF4444" name="Failed" />
                  <Bar dataKey="skipped" stackId="a" fill="#F59E0B" name="Skipped" />
                  <Bar dataKey="pending" stackId="a" fill="#94A3B8" name="Pending" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardBody>
        </Card>

        <Card borderRadius="2xl">
          <CardHeader>
            <HStack>
              <ShieldAlert size={18} />
              <Heading size="md">By type / trigger</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <Text fontWeight="semibold" mb={2}>By kind</Text>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={kindChart}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={11} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#0EA5E9" name="Count" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              <Box>
                <Text fontWeight="semibold" mb={2}>Top triggers</Text>
                <VStack align="stretch" spacing={2}>
                  {triggerChart.map((row) => (
                    <HStack key={row.name} justify="space-between">
                      <Text fontSize="sm">{row.name}</Text>
                      <Badge>{row.value}</Badge>
                    </HStack>
                  ))}
                  {triggerChart.length === 0 && <Text color="gray.500">No data yet</Text>}
                </VStack>
              </Box>
            </SimpleGrid>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Card borderRadius="2xl">
        <CardHeader>
          <HStack justify="space-between">
            <HStack>
              <Filter size={18} />
              <Heading size="md">All interactions</Heading>
            </HStack>
            <Badge>{total} total</Badge>
          </HStack>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 5 }} spacing={3} mb={4}>
            <FormControl>
              <FormLabel fontSize="sm">Kind</FormLabel>
              <Select value={kind} onChange={(e) => { setPage(0); setKind(e.target.value) }}>
                <option value="">All</option>
                <option value="VEHICLE">VEHICLE</option>
                <option value="DRIVER">DRIVER</option>
                <option value="TRIP">TRIP</option>
                <option value="DAILY_REPORT">DAILY_REPORT</option>
                <option value="AUTH_PROBE">AUTH_PROBE</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Status</FormLabel>
              <Select value={status} onChange={(e) => { setPage(0); setStatus(e.target.value) }}>
                <option value="">All</option>
                <option value="SUCCESS">SUCCESS</option>
                <option value="FAILED">FAILED</option>
                <option value="SKIPPED">SKIPPED</option>
                <option value="PENDING">PENDING</option>
                <option value="PROCESSING">PROCESSING</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">From</FormLabel>
              <Input type="date" value={from} onChange={(e) => { setPage(0); setFrom(e.target.value) }} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">To</FormLabel>
              <Input type="date" value={to} onChange={(e) => { setPage(0); setTo(e.target.value) }} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Search</FormLabel>
              <Input
                placeholder="entity, trigger, message..."
                value={search}
                onChange={(e) => { setPage(0); setSearch(e.target.value) }}
              />
            </FormControl>
          </SimpleGrid>

          {listQuery.isLoading ? (
            <HStack py={8} justify="center"><Spinner /></HStack>
          ) : items.length === 0 ? (
            <Alert status="info" borderRadius="lg">
              <AlertIcon />
              No LATRA interactions match these filters yet.
            </Alert>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>When</Th>
                    <Th>Kind</Th>
                    <Th>Status</Th>
                    <Th>Entity</Th>
                    <Th>Trigger</Th>
                    <Th>LATRA</Th>
                    <Th>Auth</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {items.map((row) => (
                    <Tr key={row.id} _hover={{ bg: 'gray.50' }}>
                      <Td whiteSpace="nowrap">{formatWhen(row.createdAt)}</Td>
                      <Td><Badge variant="outline">{row.kind}</Badge></Td>
                      <Td><Badge colorScheme={statusColor(row.status)}>{row.status}</Badge></Td>
                      <Td maxW="180px" isTruncated title={row.entityId}>{row.entityId}</Td>
                      <Td maxW="140px" isTruncated>{row.trigger || '—'}</Td>
                      <Td>
                        {row.latraStatusCode != null ? `${row.latraStatusCode}` : '—'}
                        {row.latraMessage ? ` · ${row.latraMessage}` : ''}
                      </Td>
                      <Td>
                        {row.authAccepted === true && <CheckCircle2 size={16} color="#22C55E" />}
                        {row.authAccepted === false && <XCircle size={16} color="#EF4444" />}
                        {row.authAccepted == null && '—'}
                      </Td>
                      <Td>
                        <Button size="xs" leftIcon={<Eye size={14} />} onClick={() => openDetail(row.id)}>
                          View
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          <HStack justify="space-between" mt={4}>
            <Text fontSize="sm" color="gray.600">
              Page {page + 1} of {totalPages}
            </Text>
            <HStack>
              <Button size="sm" isDisabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                Previous
              </Button>
              <Button
                size="sm"
                isDisabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </HStack>
          </HStack>
        </CardBody>
      </Card>

      <Drawer isOpen={detailDrawer.isOpen} placement="right" onClose={detailDrawer.onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>LATRA interaction detail</DrawerHeader>
          <DrawerBody>
            {detailQuery.isLoading && <Spinner />}
            {selected && (
              <VStack align="stretch" spacing={4} pb={8}>
                <SimpleGrid columns={2} spacing={3}>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Status</Text>
                    <Badge colorScheme={statusColor(selected.status)}>{selected.status}</Badge>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Kind</Text>
                    <Text fontWeight="semibold">{selected.kind}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Entity ID</Text>
                    <Text fontSize="sm" wordBreak="break-all">{selected.entityId}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Trigger</Text>
                    <Text fontSize="sm">{selected.trigger || '—'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Created</Text>
                    <Text fontSize="sm">{formatWhen(selected.createdAt)}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Completed</Text>
                    <Text fontSize="sm">{formatWhen(selected.completedAt)}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">HTTP / LATRA code</Text>
                    <Text fontSize="sm">
                      {selected.httpStatus ?? '—'} / {selected.latraStatusCode ?? '—'}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">LATRA reference</Text>
                    <Text fontSize="sm">{selected.latraReference || '—'}</Text>
                  </Box>
                </SimpleGrid>

                <Box>
                  <Text fontSize="xs" color="gray.500">Message</Text>
                  <Text fontSize="sm">{selected.latraMessage || selected.errorMessage || '—'}</Text>
                </Box>

                <Divider />
                <Box>
                  <Text fontWeight="semibold" mb={2}>Request payload (saved locally)</Text>
                  <Textarea value={pretty(selected.requestPayload)} minH="180px" fontFamily="mono" fontSize="xs" isReadOnly />
                </Box>
                <Box>
                  <Text fontWeight="semibold" mb={2}>LATRA response (saved locally)</Text>
                  <Textarea value={pretty(selected.responseBody)} minH="180px" fontFamily="mono" fontSize="xs" isReadOnly />
                </Box>
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </VStack>
  )
}
